import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageLayout from '../../components/layout/PageLayout';
import DataTable from '../../components/ui/DataTable';
import StatusChip from '../../components/ui/StatusChip';
import ActionIconButton from '../../components/ui/ActionIconButton';
import { notify, getErrorMessage } from '../../components/feedback/notify';
import { FormatDate } from "../../utils/FormatDate";
import EditarDesconto from "./components/EditarDesconto";
import AdicionarDesconto from "./components/AdicionarDesconto";

function Descontos() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [descontos, setDescontos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alterandoStatus, setAlterandoStatus] = useState(null);
    const [editarDesconto, setEditarDesconto] = useState(false);
    const [adicionarDesconto, setAdicionarDesconto] = useState(false);
    const [idDesconto, setIdDesconto] = useState(null);

    const getDescontos = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarDescontos`, {
                headers: {
                    Authorization: token
                }
            });
            setDescontos(response.data.descontos || []);
        } catch (error) {
            console.error("Erro ao listar descontos:", error);
            notify.error("Não foi possível carregar os descontos.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Recarrega a lista ao abrir/fechar os diálogos (mesmo comportamento anterior).
    useEffect(() => {
        getDescontos();
    }, [getDescontos, editarDesconto, adicionarDesconto]);

    const handleOpenEditarDesconto = (idDesconto) => {
        setIdDesconto(idDesconto);
        setEditarDesconto(true);
    }

    const alterarStatus = async (idDesconto, rota, mensagem) => {
        try {
            setAlterandoStatus(idDesconto);
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/${rota}/${idDesconto}`, {}, {
                headers: {
                    Authorization: token
                }
            });
            if (Array.isArray(response.data.descontos)) {
                setDescontos(response.data.descontos);
            } else {
                await getDescontos();
            }
            notify.success(mensagem);
        } catch (error) {
            console.error("Erro ao alterar status do desconto:", error);
            notify.error(getErrorMessage(error, "Não foi possível alterar o status do desconto."));
        } finally {
            setAlterandoStatus(null);
        }
    }

    const handleDesabilitarDesconto = (idDesconto) => alterarStatus(idDesconto, 'desabilitarDesconto', 'Desconto desabilitado.');

    const handleHabilitarDesconto = (idDesconto) => alterarStatus(idDesconto, 'habilitarDesconto', 'Desconto habilitado.');

    const columns = [
        { key: 'idDesconto', label: 'ID', width: 72 },
        { key: 'discountCode', label: 'Código', render: (d) => <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, fontSize: '0.8125rem' }}>{d?.discountCode}</Box> },
        { key: 'valorDesconto', label: 'Desconto', render: (d) => `${d?.valorDesconto ?? 0}%` },
        { key: 'duracaoMeses', label: 'Duração', render: (d) => `${d?.duracaoMeses ?? 0} ${Number(d?.duracaoMeses) === 1 ? 'mês' : 'meses'}` },
        { key: 'updatedAt', label: 'Última alteração', render: (d) => (d?.updatedAt ? FormatDate(d.updatedAt) : '—') },
        { key: 'status', label: 'Status', render: (d) => <StatusChip label={d?.status ? 'Ativo' : 'Inativo'} tone={d?.status ? 'success' : 'neutral'} /> },
        {
            key: 'acoes',
            label: 'Ações',
            align: 'right',
            width: 150,
            render: (desconto) => (
                <Box sx={{ display: 'inline-flex', gap: 0.75 }}>
                    <ActionIconButton title="Editar" icon={<EditOutlinedIcon fontSize="small" />} onClick={() => handleOpenEditarDesconto(desconto.idDesconto)} />
                    <ActionIconButton title="Clientes que usaram" icon={<GroupOutlinedIcon fontSize="small" />} onClick={() => navigate(`../DescontosUsados/${desconto.idDesconto}`)} />
                    {desconto.status ? (
                        <ActionIconButton
                            title="Desabilitar"
                            color="error"
                            icon={<BlockOutlinedIcon fontSize="small" />}
                            disabled={alterandoStatus === desconto.idDesconto}
                            onClick={() => handleDesabilitarDesconto(desconto.idDesconto)}
                        />
                    ) : (
                        <ActionIconButton
                            title="Habilitar"
                            color="success"
                            icon={<CheckCircleOutlineIcon fontSize="small" />}
                            disabled={alterandoStatus === desconto.idDesconto}
                            onClick={() => handleHabilitarDesconto(desconto.idDesconto)}
                        />
                    )}
                </Box>
            ),
        },
    ];

    return (
        <PageLayout
            title="Descontos"
            subtitle="Cupons de desconto disponíveis para novas assinaturas."
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Descontos' }]}
            actions={
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdicionarDesconto(true)}>
                    Adicionar desconto
                </Button>
            }
        >
            <DataTable
                columns={columns}
                rows={descontos}
                getRowKey={(d, i) => d?.idDesconto ?? i}
                loading={loading}
                emptyMessage="Nenhum desconto cadastrado."
            />
            <EditarDesconto editarDesconto={editarDesconto} setEditarDesconto={setEditarDesconto} idDesconto={idDesconto} />
            <AdicionarDesconto adicionarDesconto={adicionarDesconto} setAdicionarDesconto={setAdicionarDesconto} />
        </PageLayout>
    );
}

export default Descontos;
